// NativeBoot — native init for the Capacitor shell (runs once; hard no-op in a
// web browser). (1) sends the initial load into the DVAuction app (the store
// app's home); (2) locks the status bar to light text on the midnight ground;
// (3) wires the Android hardware back button to router-back (exit at the root).
//
// ⚠️ THIS WAS `src/components/mobile/MobileBoot.tsx` AND IT DID NOT BELONG THERE.
// It has nothing to do with the retired `/app` shell that filled that folder —
// it is the boot path for the native app, rendered unconditionally in App.tsx
// on every platform. Deleting the folder around it would have broken the native
// launch, status bar and Android back button, and nothing would have caught it:
// none of it runs in a browser, so the whole file is a silent no-op in every
// test and every preview. That is also why the boot path is asserted from the
// SOURCE in NativeBoot.test.ts rather than by rendering it. Moved out on 2026-09-18 with the `/app`
// retirement so its name says what it is and its location keeps it out of the
// next cull.
//
// ⚠️ A NATIVE LAUNCH OPENS THE RAISEDBEEF APP (`/m`), CHANGED 2026-09-20.
// It used to open `claraPath()` — the DVAuction app — and that was half of why
// product 1's phone app could not be opened as an app at all.
//
// Owner: "We aren\'t using the DVauction app anymore" / "Yes DVauction is
// retired". Three things say this is now simply correct rather than a
// re-pointing:
//   1. the ROOT capacitor.config.ts already declares appId
//      `com.raisedbeef.app`, appName "RaisedBeef" — this project was always
//      meant to be the RaisedBeef app;
//   2. DVAuction has its OWN flavour at native/dvauction/, exactly like the
//      barn apps, so it never needed the root project;
//   3. that flavour\'s appId is still marked "PLACEHOLDER — DVAuction must
//      confirm before first Play upload", so it was never published.
//
// An already-installed Capacitor app carries its own bundled web assets, so
// this changes nothing for anything already on a phone. It changes what the
// NEXT native build opens.
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

/** The RaisedBeef phone app's mount. Matches the route in App.tsx. */
const RB_APP_PATH = "/m";

export default function NativeBoot() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Native launch lands in the RaisedBeef app. A literal path, not a
    // host-derived one: in the WebView the hostname is localhost, so anything
    // that resolves by host is guessing.
    if (location.pathname === "/") navigate(RB_APP_PATH, { replace: true });

    // Status bar: light content on the dark app ground; dark bg on Android.
    (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: Style.Dark }); // Dark = light icons/text
        if (Capacitor.getPlatform() === "android") {
          await StatusBar.setBackgroundColor({ color: "#0B0E13" });
        }
      } catch { /* status-bar plugin unavailable */ }
    })();

    // Android hardware back → browser/router back; exit the app at the root.
    let remove: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("backButton", ({ canGoBack }) => {
          if (canGoBack) window.history.back();
          else void App.exitApp();
        });
        remove = () => void handle.remove();
      } catch { /* @capacitor/app unavailable */ }
    })();

    return () => remove?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
